import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useTriggerLoad } from "@redotech/react-util/load";
import { FulfillmentGroupData } from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import { isOpenIsh } from "@redotech/redo-fulfillment-group-common/fulfillment-group-status";
import { useTranslation } from "@redotech/redo-i18n";
import { cancelLeavesNoItems } from "@redotech/redo-merchant-fulfillment-rpc/schema/cancel-order-items";
import { toast } from "@redotech/redo-web/alert";
import { RedoCheckbox } from "@redotech/redo-web/arbiter-components/checkbox/redo-checkbox";
import { RedoIncrementDecrement } from "@redotech/redo-web/arbiter-components/increment-decrement/redo-increment-decrement";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { Flex } from "@redotech/redo-web/flex";
import { SkeletonText } from "@redotech/redo-web/skeleton";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo, useState } from "react";
import { LineItemPickabilityBadge } from "../../fulfillment-groups/columns/pickability-column";
import { RedoOutboundLabelsRpcClientContext } from "../../outbound-labels-rpc-client";
import {
  ProductBinCountsSection,
  type ProductBinCounts,
} from "./cancel-item-bin-counts";
import { useOriginWmsLocation } from "./use-origin-wms-location";

/** Cancels and refunds some units of an order's items, leaving the rest to ship. */
export const CancelOrderItemsModal = memo(function CancelOrderItemsModal({
  fulfillmentGroup,
  onClose,
}: {
  fulfillmentGroup: FulfillmentGroupData;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const rpcClient = useRequiredContext(RedoOutboundLabelsRpcClientContext);
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [notifyCustomer, setNotifyCustomer] = useState(true);
  const [restock, setRestock] = useState(false);
  const [binCountsByProduct, setBinCountsByProduct] = useState<
    Record<string, ProductBinCounts | null>
  >({});

  // Redo owns on-hand at a WMS source-of-truth origin, so offer bin counts instead of a Shopify restock.
  const {
    originLocationId,
    wmsSot,
    loading: originLocationLoading,
    error: originLocationError,
  } = useOriginWmsLocation(fulfillmentGroup);

  // Bundle components cancel with their bundle, so only the ordered lines are offered.
  const lines = useMemo(
    () =>
      fulfillmentGroup.items.filter(
        (item) => !item.bundleParentLineItemId && item.quantity > 0,
      ),
    [fulfillmentGroup.items],
  );

  // Same gate as the pickability column: it's only meaningful for open-like groups.
  const pickabilityByExternalId = useMemo(
    () =>
      new Map(
        isOpenIsh(fulfillmentGroup.status)
          ? (fulfillmentGroup.lineItemPickability ?? []).map((item) => [
              item.externalId,
              item,
            ])
          : [],
      ),
    [fulfillmentGroup.status, fulfillmentGroup.lineItemPickability],
  );

  const selectedLines = lines.filter(
    (line) => (quantities[line.externalId] ?? 0) > 0,
  );
  const leavesNoItems = cancelLeavesNoItems(
    fulfillmentGroup.items.map((item) => item.quantity),
    selectedLines.map((line) => quantities[line.externalId] ?? 0),
  );

  const selectedProducts = useMemo(() => {
    const byProduct = new Map<
      string,
      { productTitle: string; defaultBinName: string | null | undefined }
    >();
    for (const line of selectedLines) {
      if (line.redoProductId && !byProduct.has(line.redoProductId)) {
        byProduct.set(line.redoProductId, {
          productTitle: line.productTitle,
          defaultBinName: line.bin,
        });
      }
    }
    return [...byProduct.entries()];
  }, [selectedLines]);

  const handleProductBinCountsChange = useHandler(
    (productId: string, binCounts: ProductBinCounts | null) =>
      setBinCountsByProduct((current) => ({
        ...current,
        [productId]: binCounts,
      })),
  );

  const [cancelLoad, triggerCancel] = useTriggerLoad(async (signal) => {
    const toastOptions = { duration: Temporal.Duration.from({ seconds: 3 }) };
    const productBinCounts = wmsSot
      ? selectedProducts.flatMap(
          ([productId]) => binCountsByProduct[productId] ?? [],
        )
      : null;
    try {
      const result = await rpcClient.cancelOrderItems(
        {
          fulfillmentGroupId: fulfillmentGroup._id,
          items: selectedLines.map((line) => ({
            fulfillmentOrderLineItemId: line.externalId,
            quantity: quantities[line.externalId] ?? 0,
          })),
          restock: wmsSot ? false : restock,
          notifyCustomer,
          productBinCounts,
        },
        { signal },
      );
      if (result.success && result.binCountsFailed) {
        toast(
          t(
            "Cancelled and refunded the items, but the bin counts could not be updated",
          ),
          { ...toastOptions, variant: "warning" },
        );
      } else if (result.success) {
        toast(t("Cancelled and refunded the selected items"), {
          ...toastOptions,
          variant: "success",
        });
      } else {
        toast(t("Failed to cancel items"), {
          ...toastOptions,
          variant: "error",
        });
      }
    } catch {
      toast(t("Failed to cancel items"), { ...toastOptions, variant: "error" });
    } finally {
      onClose();
    }
  });

  return (
    <RedoModal
      isOpen
      modalSize={wmsSot ? "md" : "sm"}
      onModalCloseRequested={onClose}
      primaryButton={{
        onClick: () => triggerCancel(),
        pending: cancelLoad.pending,
        disabled: leavesNoItems
          ? t(
              "This would leave no items on the order. Cancel the order instead.",
            )
          : originLocationLoading ||
            originLocationError ||
            selectedLines.length === 0,
        text: t("Cancel & refund"),
        theme: "destructive",
        size: "md",
      }}
      secondaryButton={{ onClick: onClose, text: t("Back"), size: "md" }}
      title={t("Cancel & refund items")}
    >
      {originLocationLoading ? (
        <Flex dir="column" gap="md">
          {lines.map((line) => (
            <SkeletonText height="32px" key={line.externalId} length="full" />
          ))}
        </Flex>
      ) : originLocationError ? (
        <Text fontSize="sm" textColor="error">
          {t("Couldn't load this order's location. Try again.")}
        </Text>
      ) : (
        <Flex dir="column" gap="lg">
          <Text fontSize="sm" textColor="secondary">
            {t(
              "Choose how many of each item to remove from the order. The customer is refunded for them.",
            )}
          </Text>
          <Flex dir="column" gap="md">
            {lines.map((line) => {
              const pickability = pickabilityByExternalId.get(line.externalId);
              return (
                <Flex
                  align="center"
                  gap="md"
                  justify="space-between"
                  key={line.externalId}
                >
                  <Flex dir="column" gap="xxs">
                    <Text fontSize="sm">{line.productTitle}</Text>
                    {(line.variantTitle || line.sku) && (
                      <Text fontSize="xs" textColor="secondary">
                        {[line.variantTitle, line.sku]
                          .filter(Boolean)
                          .join(" · ")}
                      </Text>
                    )}
                    {pickability && (
                      <Flex>
                        <LineItemPickabilityBadge item={pickability} />
                      </Flex>
                    )}
                  </Flex>
                  <Flex align="center" gap="sm">
                    <RedoIncrementDecrement
                      max={line.quantity}
                      min={0}
                      setValue={(value) =>
                        setQuantities((current) => ({
                          ...current,
                          [line.externalId]: value,
                        }))
                      }
                      size="small"
                      value={quantities[line.externalId] ?? 0}
                      widthHint="1000"
                    />
                    <Text fontSize="sm" textColor="secondary">
                      {t("of {{quantity}}", { quantity: line.quantity })}
                    </Text>
                  </Flex>
                </Flex>
              );
            })}
          </Flex>
          {leavesNoItems && (
            <Text fontSize="sm" textColor="error">
              {t(
                "This would leave no items on the order. Cancel the order instead.",
              )}
            </Text>
          )}
          <RedoCheckbox
            label={t("Notify customer")}
            setValue={(value) => setNotifyCustomer(value === true)}
            size="sm"
            value={notifyCustomer}
          />
          {wmsSot ? (
            selectedProducts.length > 0 && (
              <Flex dir="column" gap="md">
                <Flex dir="column" gap="xxs">
                  <Text fontSize="sm" fontWeight="semibold">
                    {t("Adjust bin inventory (optional)")}
                  </Text>
                  <Text fontSize="xs" textColor="secondary">
                    {t(
                      "Set the counted quantity for bins that don't have this item.",
                    )}
                  </Text>
                </Flex>
                {selectedProducts.map(
                  ([productId, { productTitle, defaultBinName }]) => (
                    <ProductBinCountsSection
                      crdbLocationId={originLocationId}
                      defaultBinName={defaultBinName}
                      key={productId}
                      onChange={handleProductBinCountsChange}
                      productId={productId}
                      productTitle={productTitle}
                    />
                  ),
                )}
              </Flex>
            )
          ) : (
            <RedoCheckbox
              label={t("Restock items")}
              setValue={(value) => setRestock(value === true)}
              size="sm"
              value={restock}
            />
          )}
        </Flex>
      )}
    </RedoModal>
  );
});
